import {
  SelectionChangeResponse,
  Tree,
  TreeSearch,
  TreeSummary,
  TypeCounter,
  TreeGroup,
  TreeItem,
  Spinner,
} from "@rio-cloud/rio-uikit";
import { isEmpty } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useAssetTree, extractFleetGroup, extractAssetCode, IFilterOptions } from "../useAssetTree";

interface IVehicleTree {
  filterOptions: IFilterOptions;
  selectedVehicles: string[];
  setSelectedVehicles: (items: string[]) => void;
}

const VehicleTree = ({ filterOptions, selectedVehicles, setSelectedVehicles }: IVehicleTree) => {
  const [searchValue, setSearchValue] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [localSelectedGroups, setLocalSelectedGroups] = useState<string[]>([]);

  const {
    // Novos dados do endpoint /fleet-groups
    fleetGroupsData,
    processedVehiclesFromApi,
    isLoadingFleetGroups,
  } = useAssetTree();

  // Decide qual fonte de dados usar: API (novo) ou parsing de nomes (legado)
  const useApiData = fleetGroupsData?.has_sync_data && processedVehiclesFromApi.length > 0;

  // Processa veículos - usa API se disponível, senão faz parsing do nome
  const processedVehicles = useMemo(() => {
    // Se temos dados da API, usa eles
    if (useApiData) {
      return processedVehiclesFromApi.map((v) => ({
        id: v.id,
        name: v.name,
        code: v.licensePlate || v.name,
        group: v.fleetGroupName || "Desagrupadas",
      }));
    }

    // Fallback: parsing do nome (método legado)
    if (!filterOptions?.vehicles?.length) return [];

    return filterOptions.vehicles.map((vehicle) => ({
      id: vehicle.id,
      name: vehicle.label,
      group: extractFleetGroup(vehicle.label),
      code: extractAssetCode(vehicle.label),
    }));
  }, [useApiData, processedVehiclesFromApi, filterOptions?.vehicles]);

  // Mapa de grupo -> veículos (para lookup rápido)
  const vehiclesByGroup = useMemo(() => {
    const map = new Map<string, string[]>();
    processedVehicles.forEach((v) => {
      // Inclui todos os grupos, inclusive "Desagrupadas"
      const existing = map.get(v.group) || [];
      existing.push(v.id);
      map.set(v.group, existing);
    });
    return map;
  }, [processedVehicles]);

  // Filtra por busca
  const filteredVehicles = useMemo(() => {
    if (!searchValue) return processedVehicles;

    return processedVehicles.filter(
      (v) =>
        v.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        v.group.toLowerCase().includes(searchValue.toLowerCase()) ||
        v.code.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [processedVehicles, searchValue]);

  // Helper para criar ID de grupo com prefixo de ordenação
  const getGroupId = (groupName: string) => {
    const isUngrouped = groupName === "Desagrupadas" || groupName === "Sem grupo";
    // Prefixo "9" para Desagrupadas garante que fique por último na ordenação alfabética
    return isUngrouped ? `group-9-${groupName}` : `group-0-${groupName}`;
  };

  // Helper para criar nome de exibição
  // Desagrupadas recebe caractere invisível U+FEFF (Zero Width No-Break Space) como prefixo
  // U+FEFF (65279) ordena após todas as letras ASCII (max 122 = 'z')
  // Isso faz "Desagrupadas" aparecer por último sem caractere visível
  const INVISIBLE_SORT_PREFIX = "\uFEFF";
  const getGroupDisplayName = (groupName: string, count: number) => {
    const isUngrouped = groupName === "Desagrupadas" || groupName === "Sem grupo";
    return isUngrouped ? `${INVISIBLE_SORT_PREFIX}${groupName} (${count})` : `${groupName} (${count})`;
  };

  // Cria lista de grupos únicos
  const groups = useMemo<TreeGroup[]>(() => {
    const groupNames = new Set<string>();

    filteredVehicles.forEach((v) => {
      groupNames.add(v.group);
    });

    return Array.from(groupNames)
      .sort((a, b) => {
        // "Desagrupadas" sempre por último
        if (a === "Desagrupadas" || a === "Sem grupo") return 1;
        if (b === "Desagrupadas" || b === "Sem grupo") return -1;
        return a.localeCompare(b);
      })
      .map((groupName) => {
        const count = filteredVehicles.filter((v) => v.group === groupName).length;
        return {
          id: getGroupId(groupName),
          name: getGroupDisplayName(groupName, count),
        };
      });
  }, [filteredVehicles]);

  // Cria lista de items com groupIds
  const items = useMemo<TreeItem[]>(() => {
    return filteredVehicles
      .sort((a, b) => {
        if (a.group !== b.group) {
          if (a.group === "Desagrupadas" || a.group === "Sem grupo") return 1;
          if (b.group === "Desagrupadas" || b.group === "Sem grupo") return -1;
          return a.group.localeCompare(b.group);
        }
        return a.code.localeCompare(b.code);
      })
      .map((vehicle) => ({
        type: "truck",
        id: vehicle.id,
        name: vehicle.name,
        groupIds: [getGroupId(vehicle.group)],
      }));
  }, [filteredVehicles]);

  // Conta total de veículos
  const totalVehicles = processedVehicles.length;

  const handleSelectionChange = useCallback(
    ({ items: selectedItems, groups: selectedGroupIds }: SelectionChangeResponse) => {
      // Atualiza grupos selecionados localmente
      setLocalSelectedGroups(selectedGroupIds);

      // IDs de veículos selecionados diretamente
      const directlySelectedVehicles = selectedItems.filter((id) => !id.startsWith("group-"));

      // IDs de veículos que pertencem a grupos selecionados
      const vehiclesFromGroups: string[] = [];
      selectedGroupIds.forEach((groupId) => {
        // Remove prefixo "group-0-" ou "group-9-" para obter o nome do grupo
        const groupName = groupId.replace(/^group-[09]-/, "");
        const vehicleIds = vehiclesByGroup.get(groupName) || [];
        vehiclesFromGroups.push(...vehicleIds);
      });

      // Combina e remove duplicados
      const allSelectedVehicles = [...new Set([...directlySelectedVehicles, ...vehiclesFromGroups])];

      setSelectedVehicles(allSelectedVehicles);
    },
    [setSelectedVehicles, vehiclesByGroup]
  );

  const handleExpandGroupsChange = useCallback((newExpandedGroups: string[]) => {
    setExpandedGroups(newExpandedGroups);
  }, []);

  const handleUpdateSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  // Mostra loading enquanto carrega fleet groups
  if (isLoadingFleetGroups && !processedVehicles.length) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
        <Spinner />
      </div>
    );
  }

  return (
    <Tree
      items={items}
      groups={groups}
      virtualizeThreshold={1000}
      expandedGroups={expandedGroups}
      hasMultiselect={!isEmpty(items)}
      selectedItems={selectedVehicles}
      selectedGroups={localSelectedGroups}
      onSelectionChange={handleSelectionChange}
      onExpandGroupsChange={handleExpandGroupsChange}
      summary={
        <TreeSummary>
          <TypeCounter icon="rioglyph rioglyph-truck" value={totalVehicles} />
        </TreeSummary>
      }
      search={<TreeSearch value={searchValue} placeholder="busca em veículo ou grupo" onChange={handleUpdateSearch} />}
    />
  );
};

export default VehicleTree;
